import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { customerAPI } from '../../api/customers';
import { CreateCustomerRequest } from '../../types/api';
import toast from 'react-hot-toast';

export const CustomerForm: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateCustomerRequest>({
    defaultValues: {
      status: 'ACTIVE',
    },
  });

  useEffect(() => {
    if (isEdit && id) {
      loadCustomer(parseInt(id));
    }
  }, [id, isEdit]);

  const loadCustomer = async (customerId: number) => {
    try {
      const customer = await customerAPI.getById(customerId);
      reset({
        name: customer.name,
        phone: customer.phone || '',
        address: customer.address || '',
        contactPerson: customer.contactPerson || '',
        status: customer.status || 'ACTIVE',
      });
    } catch (error) {
      toast.error(t('customers.loadError'));
      navigate('/customers');
    }
  };

  const onSubmit = async (data: CreateCustomerRequest) => {
    setLoading(true);
    try {
      if (isEdit && id) {
        await customerAPI.update(parseInt(id), data);
        toast.success(t('customers.updateSuccess'));
      } else {
        await customerAPI.create(data);
        toast.success(t('customers.createSuccess'));
      }
      navigate('/customers');
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('errors.unknown'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">
        {isEdit ? t('customers.editCustomer') : t('customers.addNew')}
      </h1>

      <form onSubmit={handleSubmit(onSubmit)} className="card space-y-4">
        <div>
          <label className="block text-sm font-medium mb-1">
            {t('customers.name')} <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            {...register('name', { required: t('validation.required') })}
            className="input-field"
            placeholder={t('customers.name')}
          />
          {errors.name && (
            <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">{t('customers.phone')}</label>
          <input
            type="text"
            {...register('phone')}
            className="input-field"
            placeholder={t('customers.phone')}
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">{t('customers.contactPerson')}</label>
          <input
            type="text"
            {...register('contactPerson')}
            className="input-field"
            placeholder={t('customers.contactPersonPlaceholder')}
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">{t('customers.address')}</label>
          <textarea
            {...register('address')}
            className="input-field"
            rows={3}
            placeholder={t('customers.address')}
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">{t('common.status')}</label>
          <select {...register('status')} className="input-field">
            <option value="ACTIVE">{t('common.active')}</option>
            <option value="INACTIVE">{t('common.inactive')}</option>
          </select>
        </div>

        <div className="flex justify-end space-x-3 pt-4">
          <button
            type="button"
            onClick={() => navigate('/customers')}
            className="btn-secondary"
          >
            {t('common.cancel')}
          </button>
          <button type="submit" disabled={loading} className="btn-primary">
            {loading ? t('common.loading') : isEdit ? t('common.save') : t('common.add')}
          </button>
        </div>
      </form>
    </div>
  );
};
